import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const EvaluationModal = ({ isOpen, onClose, onSubmitSuccess, answers, questions, competencies }) => {
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [agreedPolicy, setAgreedPolicy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submissionComplete, setSubmissionComplete] = useState(false);

  if (!isOpen) return null;

  // Helper to create temporary PDF pages
  const createPDFPage = (htmlContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 800px;
      background: white;
      padding: 30px;
      font-family: Arial, sans-serif;
      color: #2c3e50;
    `;
    tempDiv.innerHTML = htmlContent;
    document.body.appendChild(tempDiv);
    return tempDiv;
  };

  // Build complete PDF with html2canvas
  const buildPDFDataUrl = async (competencyScores) => {
    try {
      const isMobile = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: !isMobile,
        putOnlyUsedFonts: true,
        hotfixes: ['px_scaling']
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = isMobile ? 15 : 10;

      const baseH2C = {
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1200,
        windowHeight: 1697,
        removeContainer: true,
        scale: isMobile ? 1.3 : 1.8,
      };

      const currentDate = new Date().toLocaleDateString('hu-HU');

      // Group scores by section
      const sections = {};
      Object.keys(competencies).forEach(competencyKey => {
        const comp = competencies[competencyKey];
        if (!sections[comp.section]) {
          sections[comp.section] = [];
        }
        const score = competencyScores[competencyKey]?.score || 0;
        sections[comp.section].push({
          name: comp.name,
          score: score
        });
      });

      // Title page
      const titlePageContent = `
        <div style="text-align: center; padding: 25px 0;">
          <h1 style="font-size: 28px; margin-bottom: 20px; color: #2c3e50; border-bottom: 3px solid #156B5C; padding-bottom: 12px;">
            Vezetői Kompetencia Felmérés
          </h1>
          <p style="font-size: 16px; margin: 15px 0; color: #7f8c8d;">Név: ${userName}</p>
          <p style="font-size: 16px; margin: 15px 0; color: #7f8c8d;">Értékelés dátuma: ${currentDate}</p>

          <div style="border: 2px solid #bdc3c7; border-radius: 8px; padding: 25px; margin: 25px 0; background: #f8f9fa;">
            <h2 style="font-size: 22px; margin-bottom: 20px; color: #2c3e50;">Összegző Eredmények</h2>
            ${Object.keys(sections).map(sectionName => `
              <div style="margin: 20px 0; text-align: left;">
                <h3 style="font-size: 18px; color: #156B5C; margin-bottom: 10px;">${sectionName}</h3>
                ${sections[sectionName].map(comp => `
                  <p style="font-size: 14px; margin: 8px 0; display: flex; justify-content: space-between;">
                    <span><strong>${comp.name}:</strong></span>
                    <span>${comp.score.toFixed(1)}/5 pont</span>
                  </p>
                `).join('')}
              </div>
            `).join('')}
          </div>
        </div>
      `;

      const titlePage = createPDFPage(titlePageContent);
      const titleCanvas = await html2canvas(titlePage, baseH2C);
      document.body.removeChild(titlePage);

      const titleImgData = titleCanvas.toDataURL('image/jpeg', 0.7);
      const imgWidth = pageWidth - 2*margin;
      const imgHeight = (titleCanvas.height * imgWidth) / titleCanvas.width;
      const pageSpace = pageHeight - 2 * margin;

      if (imgHeight > pageSpace) {
        let heightLeft = imgHeight;
        let sourceY = 0;
        let isFirstSlice = true;
        while (heightLeft > 0) {
          const sliceHeight = Math.min(heightLeft, pageSpace);
          const sourceHeight = (sliceHeight * titleCanvas.height) / imgHeight;
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = titleCanvas.width;
          tempCanvas.height = sourceHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(titleCanvas, 0, sourceY, titleCanvas.width, sourceHeight, 0, 0, titleCanvas.width, sourceHeight);
          const sliceData = tempCanvas.toDataURL('image/jpeg', 0.7);

          if (!isFirstSlice) pdf.addPage();
          pdf.addImage(sliceData, 'JPEG', margin, margin, imgWidth, sliceHeight);

          heightLeft -= sliceHeight;
          sourceY += sourceHeight;
          isFirstSlice = false;
        }
      } else {
        pdf.addImage(titleImgData, 'JPEG', margin, margin, imgWidth, imgHeight);
      }

      // Detailed questions pages for each competency
      for (const competencyKey of Object.keys(competencies)) {
        const comp = competencies[competencyKey];
        const compQuestions = questions.filter(q => q.competency === competencyKey);

        if (compQuestions.length === 0) continue;

        const getAnswerLabel = (val) => {
          if (val === 5) return 'Teljes mértékben';
          if (val === 4) return 'Inkább igen';
          if (val === 3) return 'Részben';
          if (val === 2) return 'Inkább nem';
          if (val === 1) return 'Egyáltalán nem';
          return '-';
        };

        const getAnswerColor = (val) => {
          if (val >= 4) return { bg: '#d4edda', color: '#155724' };
          if (val === 3) return { bg: '#fff3cd', color: '#856404' };
          return { bg: '#f8d7da', color: '#721c24' };
        };

        const questionsContent = `
          <div style="padding: 20px;">
            <h2 style="font-size: 22px; color: #156B5C; margin-bottom: 10px;">${comp.name}</h2>
            <p style="font-size: 14px; color: #7f8c8d; margin-bottom: 20px;">Szekció: ${comp.section}</p>
            <p style="font-size: 16px; margin-bottom: 20px;"><strong>Eredmény: ${(competencyScores[competencyKey]?.score || 0).toFixed(1)}/5 pont</strong></p>

            ${compQuestions.map((q, idx) => {
              const answer = answers[q.id] || 0;
              const answerText = getAnswerLabel(answer);
              const colors = getAnswerColor(answer);
              return `
                <div style="margin: 15px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; background: #fff;">
                  <p style="font-size: 14px; margin-bottom: 10px;"><strong>${idx + 1}. ${q.text}</strong></p>
                  <div style="text-align: right;">
                    <span style="background: ${colors.bg}; color: ${colors.color}; padding: 4px 10px; border-radius: 4px; font-weight: bold; font-size: 12px;">
                      ${answerText} (${answer}/5)
                    </span>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        `;

        const questionsPage = createPDFPage(questionsContent);
        const questionsCanvas = await html2canvas(questionsPage, baseH2C);
        document.body.removeChild(questionsPage);

        const qImgData = questionsCanvas.toDataURL('image/jpeg', 0.7);
        const qImgHeight = (questionsCanvas.height * imgWidth) / questionsCanvas.width;

        pdf.addPage();

        if (qImgHeight > pageSpace) {
          let heightLeft = qImgHeight;
          let sourceY = 0;
          let isFirstSlice = true;
          while (heightLeft > 0) {
            const sliceHeight = Math.min(heightLeft, pageSpace);
            const sourceHeight = (sliceHeight * questionsCanvas.height) / qImgHeight;
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = questionsCanvas.width;
            tempCanvas.height = sourceHeight;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.drawImage(questionsCanvas, 0, sourceY, questionsCanvas.width, sourceHeight, 0, 0, questionsCanvas.width, sourceHeight);
            const sliceData = tempCanvas.toDataURL('image/jpeg', 0.7);

            if (!isFirstSlice) pdf.addPage();
            pdf.addImage(sliceData, 'JPEG', margin, margin, imgWidth, sliceHeight);

            heightLeft -= sliceHeight;
            sourceY += sourceHeight;
            isFirstSlice = false;
          }
        } else {
          pdf.addImage(qImgData, 'JPEG', margin, margin, imgWidth, qImgHeight);
        }
      }

      // Thank you page
      pdf.addPage();
      const thankYouContent = `
        <div style="text-align: center; padding: 30px;">
          <h2 style="font-size: 24px; color: #2c5530; margin-bottom: 20px;">Köszönjük a teszt kitöltését!</h2>
          <div style="text-align: left; line-height: 1.8; color: #2c3e50; max-width: 600px; margin: 0 auto;">
            <p style="font-size: 14px; margin-bottom: 15px;">Ez a teszt azért készült, hogy konkrét ismereted legyen az erősségeidről és a fejlesztendő területekről. A teszt felmérte azokat a kompetenciáidat, melyek a jó vezető ismérvei, és ezért fejleszteni szükséges. A cél nem a minősítés, hanem felismerni a fókuszpontokat.</p>

            <p style="font-size: 14px; margin-bottom: 15px;">Az eredmények alapján olyan személyre szabott fejlesztési terv készíthető, amely a legnagyobb mértékben támogatja a vezetői potenciálod maximális kibontakoztatását.</p>

            <p style="font-size: 14px; margin-bottom: 8px;">Amennyiben érdekelnek a részletek és hogy milyen módon történik ez, keress minket bátran:</p>

            <p style="font-size: 14px; margin-bottom: 30px;"><strong>info@vistaverde.hu</strong></p>

            <p style="font-size: 14px; margin-top: 40px; font-style: italic;">
              Üdvözlettel,<br/>
              <strong>a Vista Verde csapata!</strong>
            </p>
          </div>
        </div>
      `;

      const thankYouPage = createPDFPage(thankYouContent);
      const thankYouCanvas = await html2canvas(thankYouPage, baseH2C);
      document.body.removeChild(thankYouPage);

      const thankYouImgData = thankYouCanvas.toDataURL('image/jpeg', 0.7);
      const thankYouImgHeight = (thankYouCanvas.height * imgWidth) / thankYouCanvas.width;

      if (thankYouImgHeight > pageSpace) {
        let heightLeft = thankYouImgHeight;
        let sourceY = 0;
        let isFirstSlice = true;
        while (heightLeft > 0) {
          const sliceHeight = Math.min(heightLeft, pageSpace);
          const sourceHeight = (sliceHeight * thankYouCanvas.height) / thankYouImgHeight;
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = thankYouCanvas.width;
          tempCanvas.height = sourceHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(thankYouCanvas, 0, sourceY, thankYouCanvas.width, sourceHeight, 0, 0, thankYouCanvas.width, sourceHeight);
          const sliceData = tempCanvas.toDataURL('image/jpeg', 0.7);

          if (!isFirstSlice) pdf.addPage();
          pdf.addImage(sliceData, 'JPEG', margin, margin, imgWidth, sliceHeight);

          heightLeft -= sliceHeight;
          sourceY += sourceHeight;
          isFirstSlice = false;
        }
      } else {
        pdf.addImage(thankYouImgData, 'JPEG', margin, margin, imgWidth, thankYouImgHeight);
      }

      return pdf.output('dataurlstring');

    } catch (error) {
      console.error('PDF generation error:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!userName.trim() || !userEmail.trim()) {
      alert('Kérjük, töltse ki az összes mezőt!');
      return;
    }

    if (!agreedPolicy) {
      alert('Kérjük, fogadja el az adatkezelési tájékoztatót!');
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate results
      const competencyScores = {};
      const competencyQuestions = {};

      // Group questions by competency
      questions.forEach(question => {
        if (!competencyQuestions[question.competency]) {
          competencyQuestions[question.competency] = [];
        }
        competencyQuestions[question.competency].push(question.id);
      });

      // Calculate scores
      Object.keys(competencies).forEach(competencyKey => {
        const questionIds = competencyQuestions[competencyKey] || [];
        if (questionIds.length === 0) return;

        const scores_array = questionIds.map(id => answers[id] || 0);
        const average = scores_array.reduce((sum, score) => sum + score, 0) / scores_array.length;

        competencyScores[competencyKey] = {
          name: competencies[competencyKey].name,
          score: Math.round(average * 10) / 10,
          section: competencies[competencyKey].section
        };
      });

      // Generate complete PDF with html2canvas
      const pdfDataUrl = await buildPDFDataUrl(competencyScores);

      // Submit to backend
      const response = await fetch('/.netlify/functions/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: userName,
          email: userEmail,
          answers: answers,
          results: { competencyScores },
          pdfDataUrl: pdfDataUrl,
          testVersion: 'warrior_onmagam_masok_szervezet',
          consent: agreedPolicy
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Hiba történt a mentés során');
      }

      const result = await response.json();
      console.log('Submission successful:', result);

      setSubmitMessage('Sikeres mentés! A kiértékelést a megadott e-mail címedre elküldtük.');
      setSubmissionComplete(true);

      // Don't automatically show results - user stays on thank you page

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitMessage('Hiba a kiértékelés során: ' + (error?.message || error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={submissionComplete ? null : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Kérem a teszt kiértékelését</h3>
        <div className="modal-body">
          {!submitMessage ? (
            <>
              <label style={{ display: 'block', marginBottom: 10 }}>
                Név
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Adja meg a nevét"
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                  disabled={isSubmitting}
                />
              </label>

              <label style={{ display: 'block', marginBottom: 20 }}>
                E-mail
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Adja meg az e-mail címét"
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                  disabled={isSubmitting}
                />
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '20px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <input
                  type="checkbox"
                  checked={agreedPolicy}
                  onChange={(e) => setAgreedPolicy(e.target.checked)}
                  disabled={isSubmitting}
                  style={{ marginTop: '3px', flexShrink: 0 }}
                />
                <span>
                  Elfogadom az{' '}
                  <a
                    href="https://vistaverde.hu/adatkezelesi-tajekoztato/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#156B5C', textDecoration: 'underline' }}
                  >
                    adatkezelési tájékoztatót
                  </a>
                </span>
              </label>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={onClose}
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  Mégse
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !agreedPolicy}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#156B5C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isSubmitting || !agreedPolicy) ? 'not-allowed' : 'pointer',
                    opacity: (isSubmitting || !agreedPolicy) ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Küldés...' : 'Elküld'}
                </button>
              </div>
            </>
          ) : (
            <div style={{
              background: '#f8f9fa',
              border: '2px solid #e9ecef',
              color: '#2c3e50',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <img
                src="/vista-verde-192.png"
                alt="Vista Verde Logo"
                style={{ height: '60px', width: 'auto', marginBottom: '15px' }}
              />
              <h3 style={{ color: '#2c5530', marginBottom: '20px', marginTop: '0' }}>
                Köszönjük a teszt kitöltését!
              </h3>
              <div style={{ textAlign: 'left', lineHeight: '1.6', color: '#2c3e50' }}>
                <p><strong>A kiértékelést a megadott e-mail címedre elküldtük.</strong></p>

                <p>Ez a teszt azért készült, hogy konkrét ismereted legyen az erősségeidről és a fejlesztendő területekről. A teszt felmérte azokat a kompetenciáidat, melyek a jó vezető ismérvei, és ezért fejleszteni szükséges. A cél nem a minősítés, hanem felismerni a fókuszpontokat.</p>

                <p>Amennyiben érdekel milyen módon történik ez, keress minket bátran: <strong>info@vistaverde.hu</strong></p>

                <p style={{ marginTop: '20px', fontStyle: 'italic' }}>
                  Üdvözlettel,<br/>
                  a Vista Verde csapata!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaluationModal;
